import { useEffect, useRef, useState, useCallback } from 'react';
import { SMPLRSPACE_TOKEN, endpoints } from '../config/api';

interface SmplrFloorPlanProps {
  spaceId: string;
  warehouseCode?: string;
  mode?: 'viewer' | 'editor';
  showInventory?: boolean;
  onClose?: () => void;
  onDataChange?: (data: SmplrFurnitureData[]) => void;
  onLocationClick?: (location: LocationStatus) => void;
}

interface SmplrFurnitureData {
  id: string;
  name: string;
  position: { x: number; y: number; z: number };
  rotation: number;
  dimensions: { width: number; depth: number; height: number };
  metadata?: Record<string, unknown>;
}

export interface LocationStatus {
  id: number;
  name: string;
  complete_name: string;
  quantity: number;
  product_count: number;
  fill_ratio: number;
  position?: { x: number; y: number; z: number };
}

interface QuantData {
  location: { id: number; name: string };
  quantity: number;
  product: { id: number; name: string };
}

// Smplrspace SDK types (extended)
interface SmplrSpace {
  startViewer: (options: {
    preview?: boolean;
    mode?: string;
    onReady?: () => void;
    onError?: (error: Error) => void;
  }) => void;
  enablePickingMode: (options: {
    onPick?: (data: unknown) => void;
  }) => void;
  disablePickingMode: () => void;
  getAllFurniture: () => Promise<SmplrFurnitureData[]>;
  addFurniture: (furniture: Partial<SmplrFurnitureData>) => Promise<SmplrFurnitureData>;
  removeFurniture: (id: string) => Promise<void>;
  updateFurniture: (id: string, data: Partial<SmplrFurnitureData>) => Promise<SmplrFurnitureData>;
  addDataLayer: (options: DataLayerOptions) => DataLayer;
  addPolyline: (options: PolylineOptions) => Polyline;
  addIcon: (options: IconOptions) => Icon;
  remove: () => void;
}

interface DataLayerOptions {
  id: string;
  type: 'point' | 'icon' | 'polygon' | 'polyline' | 'dotted-polyline';
  data: unknown[];
  tooltip?: (d: unknown) => string;
  onClick?: (d: unknown) => void;
  color?: string | ((d: unknown) => string);
  alpha?: number;
  height?: number;
}

interface DataLayer {
  setData: (data: unknown[]) => void;
  remove: () => void;
}

interface PolylineOptions {
  id: string;
  coordinates: Array<{ x: number; y: number; z: number }>;
  color?: string;
  width?: number;
}

interface Polyline {
  remove: () => void;
}

interface IconOptions {
  id: string;
  position: { x: number; y: number; z: number };
  icon: { url: string; width: number; height: number };
  tooltip?: string;
  onClick?: () => void;
}

interface Icon {
  remove: () => void;
}

interface SmplrJs {
  Space: new (options: {
    spaceId: string;
    clientToken: string;
    containerId: string;
  }) => SmplrSpace;
}

// Extend window type for Smplrspace SDK
declare global {
  interface Window {
    smplr?: SmplrJs;
  }
}

// Dynamic SDK loader
const loadSmplrJs = (): Promise<SmplrJs> => {
  return new Promise((resolve, reject) => {
    if (window.smplr) {
      resolve(window.smplr);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://app.smplrspace.com/lib/smplr.js';
    script.async = true;
    script.onload = () => {
      if (window.smplr) {
        resolve(window.smplr);
      } else {
        reject(new Error('Smplrspace SDK failed to initialize'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Smplrspace SDK'));
    document.head.appendChild(script);
  });
};

// Color scale for inventory heatmap
const getHeatmapColor = (fillRatio: number): string => {
  if (fillRatio <= 0) return '#333333';      // Empty - dark gray
  if (fillRatio < 0.25) return '#4caf50';    // Low - green
  if (fillRatio < 0.5) return '#ffeb3b';     // Medium - yellow
  if (fillRatio < 0.75) return '#ff9800';    // High - orange
  return '#f44336';                           // Full - red
};

export default function SmplrFloorPlan({
  spaceId,
  warehouseCode,
  mode = 'viewer',
  showInventory = false,
  onClose,
  onDataChange,
  onLocationClick,
}: SmplrFloorPlanProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const spaceRef = useRef<SmplrSpace | null>(null);
  const dataLayerRef = useRef<DataLayer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickingMode, setPickingMode] = useState(false);
  const [locationData, setLocationData] = useState<LocationStatus[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationStatus | null>(null);

  const containerId = `smplr-container-${spaceId}`;

  // Load inventory data from Odoo
  const loadInventoryData = useCallback(async () => {
    if (!warehouseCode) return;

    setInventoryLoading(true);
    try {
      // Fetch quants from Odoo API
      const response = await fetch(
        endpoints.odooQuants(warehouseCode, 'production'),
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to load inventory data');
      }

      const data = await response.json();
      const quants: QuantData[] = data.quants || [];

      // Aggregate quants by location
      const locationMap = new Map<number, LocationStatus>();

      for (const quant of quants) {
        const locId = quant.location.id;
        const existing = locationMap.get(locId);

        if (existing) {
          existing.quantity += quant.quantity;
          existing.product_count += 1;
        } else {
          locationMap.set(locId, {
            id: locId,
            name: quant.location.name,
            complete_name: quant.location.name,
            quantity: quant.quantity,
            product_count: 1,
            fill_ratio: 0,
          });
        }
      }

      // Calculate fill ratios (assuming 100 units = full for now)
      const maxCapacity = 100;
      const locations = Array.from(locationMap.values()).map(loc => ({
        ...loc,
        fill_ratio: Math.min(loc.quantity / maxCapacity, 1),
      }));

      // Sort by quantity descending
      locations.sort((a, b) => b.quantity - a.quantity);

      setLocationData(locations);

      // Update data layer if viewer is ready
      if (spaceRef.current && dataLayerRef.current) {
        updateDataLayer(locations);
      }
    } catch (err) {
      console.error('Failed to load inventory:', err);
    } finally {
      setInventoryLoading(false);
    }
  }, [warehouseCode]);

  // Update the data layer with location status
  const updateDataLayer = useCallback((locations: LocationStatus[]) => {
    if (!spaceRef.current) return;

    // For now, we'll create point markers at grid positions
    // In production, these would be mapped to actual floor coordinates
    const gridSize = Math.ceil(Math.sqrt(locations.length));
    const spacing = 3; // meters between points

    const points = locations.slice(0, 100).map((loc, index) => {
      const row = Math.floor(index / gridSize);
      const col = index % gridSize;

      return {
        ...loc,
        position: {
          x: col * spacing + 2,
          y: row * spacing + 2,
          z: 0.1,
        },
      };
    });

    if (dataLayerRef.current) {
      dataLayerRef.current.setData(points);
    } else {
      // Create new data layer
      try {
        dataLayerRef.current = spaceRef.current.addDataLayer({
          id: 'inventory-heatmap',
          type: 'point',
          data: points,
          color: (d: unknown) => {
            const loc = d as LocationStatus;
            return getHeatmapColor(loc.fill_ratio);
          },
          alpha: 0.8,
          height: 0.5,
          tooltip: (d: unknown) => {
            const loc = d as LocationStatus;
            return `${loc.name}\n${loc.quantity} units\n${loc.product_count} products`;
          },
          onClick: (d: unknown) => {
            const loc = d as LocationStatus;
            setSelectedLocation(loc);
            onLocationClick?.(loc);
          },
        });
      } catch (err) {
        console.error('Failed to create data layer:', err);
      }
    }
  }, [onLocationClick]);

  // Initialize Smplrspace viewer
  useEffect(() => {
    if (!SMPLRSPACE_TOKEN) {
      setError('Smplrspace credentials not configured');
      setLoading(false);
      return;
    }

    let mounted = true;

    const initSpace = async () => {
      try {
        const smplr = await loadSmplrJs();

        if (!mounted) return;

        const space = new smplr.Space({
          spaceId,
          clientToken: SMPLRSPACE_TOKEN,
          containerId,
        });

        space.startViewer({
          preview: false,
          mode: mode === 'editor' ? 'edit' : 'view',
          onReady: () => {
            if (mounted) {
              setLoading(false);
              spaceRef.current = space;

              // Load inventory data if enabled
              if (showInventory && warehouseCode) {
                loadInventoryData();
              }
            }
          },
          onError: (err) => {
            if (mounted) {
              setError(err.message || 'Failed to load floor plan');
              setLoading(false);
            }
          },
        });
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize Smplrspace');
          setLoading(false);
        }
      }
    };

    initSpace();

    return () => {
      mounted = false;
      if (dataLayerRef.current) {
        dataLayerRef.current.remove();
        dataLayerRef.current = null;
      }
      if (spaceRef.current) {
        spaceRef.current.remove();
        spaceRef.current = null;
      }
    };
  }, [spaceId, mode, containerId, showInventory, warehouseCode, loadInventoryData]);

  const handleTogglePicking = useCallback(() => {
    if (!spaceRef.current) return;

    if (pickingMode) {
      spaceRef.current.disablePickingMode();
      setPickingMode(false);
    } else {
      spaceRef.current.enablePickingMode({
        onPick: (data) => {
          console.log('Picked element:', data);
        },
      });
      setPickingMode(true);
    }
  }, [pickingMode]);

  const handleExportData = useCallback(async () => {
    if (!spaceRef.current) return;

    try {
      const furniture = await spaceRef.current.getAllFurniture();
      onDataChange?.(furniture);
      console.log('Floor plan furniture data:', furniture);
    } catch (err) {
      console.error('Failed to export data:', err);
    }
  }, [onDataChange]);

  const handleRefreshInventory = useCallback(() => {
    loadInventoryData();
  }, [loadInventoryData]);

  if (!SMPLRSPACE_TOKEN) {
    return (
      <div className="smplr-floor-plan-modal">
        <div className="smplr-content">
          <div className="smplr-header">
            <h3>Floor Plan Viewer</h3>
            {onClose && (
              <button className="smplr-close-btn" onClick={onClose}>×</button>
            )}
          </div>
          <div className="smplr-error">
            <p>Smplrspace is not configured.</p>
            <p className="smplr-error-detail">
              Add VITE_SMPLRSPACE_ORG_ID and VITE_SMPLRSPACE_TOKEN to your .env.local file.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="smplr-floor-plan-modal">
      <div className="smplr-content">
        <div className="smplr-header">
          <h3>
            Floor Plan {mode === 'editor' ? 'Editor' : 'Viewer'}
            {warehouseCode && <span className="smplr-warehouse-badge">{warehouseCode}</span>}
          </h3>
          <div className="smplr-header-actions">
            {showInventory && (
              <button
                className="smplr-btn"
                onClick={handleRefreshInventory}
                disabled={inventoryLoading}
                title="Refresh inventory"
              >
                {inventoryLoading ? (
                  <span className="loading-spinner-small" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                )}
              </button>
            )}
            {mode === 'editor' && (
              <>
                <button
                  className={`smplr-btn ${pickingMode ? 'active' : ''}`}
                  onClick={handleTogglePicking}
                  title="Select elements"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
                  </svg>
                </button>
                <button
                  className="smplr-btn"
                  onClick={handleExportData}
                  title="Export data"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
              </>
            )}
            {onClose && (
              <button className="smplr-close-btn" onClick={onClose}>×</button>
            )}
          </div>
        </div>

        <div className="smplr-main-container">
          <div className="smplr-viewer-container">
            {loading && (
              <div className="smplr-loading">
                <div className="loading-spinner" />
                <p>Loading floor plan...</p>
              </div>
            )}

            {error && (
              <div className="smplr-error">
                <p>{error}</p>
                <button className="smplr-btn" onClick={() => window.location.reload()}>
                  Retry
                </button>
              </div>
            )}

            <div
              id={containerId}
              ref={containerRef}
              className="smplr-embed"
              style={{ display: loading || error ? 'none' : 'block' }}
            />
          </div>

          {/* Inventory Sidebar */}
          {showInventory && !loading && !error && (
            <div className="smplr-inventory-sidebar">
              <h4>Inventory Status</h4>

              {/* Legend */}
              <div className="smplr-legend">
                <div className="smplr-legend-item">
                  <span className="smplr-legend-color" style={{ backgroundColor: '#4caf50' }} />
                  <span>Low (&lt;25%)</span>
                </div>
                <div className="smplr-legend-item">
                  <span className="smplr-legend-color" style={{ backgroundColor: '#ffeb3b' }} />
                  <span>Medium (25-50%)</span>
                </div>
                <div className="smplr-legend-item">
                  <span className="smplr-legend-color" style={{ backgroundColor: '#ff9800' }} />
                  <span>High (50-75%)</span>
                </div>
                <div className="smplr-legend-item">
                  <span className="smplr-legend-color" style={{ backgroundColor: '#f44336' }} />
                  <span>Full (&gt;75%)</span>
                </div>
              </div>

              {/* Stats */}
              <div className="smplr-stats">
                <div className="smplr-stat">
                  <span className="smplr-stat-value">{locationData.length}</span>
                  <span className="smplr-stat-label">Locations</span>
                </div>
                <div className="smplr-stat">
                  <span className="smplr-stat-value">
                    {locationData.reduce((sum, l) => sum + l.quantity, 0).toLocaleString()}
                  </span>
                  <span className="smplr-stat-label">Total Units</span>
                </div>
              </div>

              {/* Location List */}
              <div className="smplr-location-list">
                <h5>Top Locations</h5>
                {locationData.slice(0, 15).map((loc) => (
                  <div
                    key={loc.id}
                    className={`smplr-location-item ${selectedLocation?.id === loc.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedLocation(loc);
                      onLocationClick?.(loc);
                    }}
                  >
                    <span
                      className="smplr-location-indicator"
                      style={{ backgroundColor: getHeatmapColor(loc.fill_ratio) }}
                    />
                    <div className="smplr-location-info">
                      <span className="smplr-location-name">{loc.name}</span>
                      <span className="smplr-location-detail">
                        {loc.quantity.toLocaleString()} units • {loc.product_count} products
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Selected Location Detail */}
              {selectedLocation && (
                <div className="smplr-location-detail-panel">
                  <h5>{selectedLocation.name}</h5>
                  <div className="smplr-detail-row">
                    <span>Quantity:</span>
                    <span>{selectedLocation.quantity.toLocaleString()}</span>
                  </div>
                  <div className="smplr-detail-row">
                    <span>Products:</span>
                    <span>{selectedLocation.product_count}</span>
                  </div>
                  <div className="smplr-detail-row">
                    <span>Fill Level:</span>
                    <span>{(selectedLocation.fill_ratio * 100).toFixed(0)}%</span>
                  </div>
                  <div className="smplr-fill-bar">
                    <div
                      className="smplr-fill-bar-inner"
                      style={{
                        width: `${selectedLocation.fill_ratio * 100}%`,
                        backgroundColor: getHeatmapColor(selectedLocation.fill_ratio),
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {mode === 'editor' && !loading && !error && (
          <div className="smplr-footer">
            <p className="smplr-hint">
              Use the Smplrspace tools to place furniture and equipment. Click Export to sync data.
            </p>
          </div>
        )}
      </div>

      <style>{`
        .smplr-warehouse-badge {
          background: #4a90d9;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          margin-left: 8px;
        }

        .smplr-main-container {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        .smplr-viewer-container {
          flex: 1;
        }

        .smplr-inventory-sidebar {
          width: 280px;
          background: #1a1a2e;
          border-left: 1px solid #333;
          padding: 16px;
          overflow-y: auto;
        }

        .smplr-inventory-sidebar h4 {
          margin: 0 0 16px 0;
          font-size: 14px;
          color: #fff;
        }

        .smplr-inventory-sidebar h5 {
          margin: 16px 0 8px 0;
          font-size: 12px;
          color: #888;
          text-transform: uppercase;
        }

        .smplr-legend {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 16px;
        }

        .smplr-legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          color: #aaa;
        }

        .smplr-legend-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
        }

        .smplr-stats {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;
          padding: 12px;
          background: #2a2a3e;
          border-radius: 6px;
        }

        .smplr-stat {
          display: flex;
          flex-direction: column;
        }

        .smplr-stat-value {
          font-size: 18px;
          font-weight: 600;
          color: #fff;
        }

        .smplr-stat-label {
          font-size: 11px;
          color: #888;
        }

        .smplr-location-list {
          max-height: 300px;
          overflow-y: auto;
        }

        .smplr-location-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.15s;
        }

        .smplr-location-item:hover {
          background: #2a2a3e;
        }

        .smplr-location-item.selected {
          background: #3a3a5e;
        }

        .smplr-location-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .smplr-location-info {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .smplr-location-name {
          font-size: 12px;
          color: #fff;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .smplr-location-detail {
          font-size: 10px;
          color: #888;
        }

        .smplr-location-detail-panel {
          margin-top: 16px;
          padding: 12px;
          background: #2a2a3e;
          border-radius: 6px;
        }

        .smplr-location-detail-panel h5 {
          margin: 0 0 12px 0;
          color: #fff;
          text-transform: none;
        }

        .smplr-detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          margin-bottom: 6px;
        }

        .smplr-detail-row span:first-child {
          color: #888;
        }

        .smplr-detail-row span:last-child {
          color: #fff;
        }

        .smplr-fill-bar {
          height: 6px;
          background: #333;
          border-radius: 3px;
          margin-top: 8px;
          overflow: hidden;
        }

        .smplr-fill-bar-inner {
          height: 100%;
          transition: width 0.3s;
        }

        .loading-spinner-small {
          width: 14px;
          height: 14px;
          border: 2px solid #444;
          border-top-color: #4a90d9;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
